/**
 * SvgEditor — Interactive SVG editor with multi-select, lasso, keyboard shortcuts.
 */
import { useState, useEffect, useRef } from 'react';

export default function SvgEditor({ svgContent, onChange }) {
  const containerRef = useRef(null);
  const selectionRectRef = useRef(null);
  const [selected, setSelected] = useState([]); // array of elements
  const [editedSvg, setEditedSvg] = useState(svgContent);

  useEffect(() => { setEditedSvg(svgContent); }, [svgContent]);

  function saveSvg() {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) return;
    // Clean selection styles before saving
    svg.querySelectorAll('[data-selected]').forEach(s => { s.removeAttribute('data-selected'); s.style.outline = ''; s.style.outlineOffset = ''; });
    const s = svg.outerHTML;
    setEditedSvg(s);
    onChange?.(s);
  }

  function selectElements(els, add) {
    const container = containerRef.current;
    if (!add) {
      container?.querySelectorAll('[data-selected]').forEach(s => { s.removeAttribute('data-selected'); s.style.outline = ''; s.style.outlineOffset = ''; });
    }
    els.forEach(el => {
      el.setAttribute('data-selected', 'true');
      el.style.outline = '2px solid #3B82F6';
      el.style.outlineOffset = '2px';
    });
    setSelected(add ? prev => [...new Set([...prev, ...els])] : els);
  }

  function clearSelection() {
    containerRef.current?.querySelectorAll('[data-selected]').forEach(s => { s.removeAttribute('data-selected'); s.style.outline = ''; s.style.outlineOffset = ''; });
    setSelected([]);
  }

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !editedSvg?.startsWith('<svg')) return;

    container.innerHTML = editedSvg;
    const svg = container.querySelector('svg');
    if (!svg) return;

    // Remove any old selection rect
    selectionRectRef.current?.remove();
    selectionRectRef.current = null;

    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.cursor = 'crosshair';
    svg.style.userSelect = 'none';
    // Ensure viewBox exists for proper scaling
    if (!svg.getAttribute('viewBox')) {
      const w = svg.getAttribute('width') || 200;
      const h = svg.getAttribute('height') || 200;
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    }
    svg.removeAttribute('width');
    svg.removeAttribute('height');

    const interactable = svg.querySelectorAll('circle, ellipse, rect:not([data-sel-rect]), path, line, text, polygon, g');
    interactable.forEach(el => {
      el.style.cursor = 'pointer';

      el.addEventListener('mousedown', (e) => {
        e.stopPropagation();
        const isShift = e.shiftKey;

        if (isShift) {
          // Add/remove from selection
          if (el.hasAttribute('data-selected')) {
            el.removeAttribute('data-selected');
            el.style.outline = '';
            setSelected(prev => prev.filter(p => p !== el));
          } else {
            selectElements([el], true);
          }
          return;
        }

        // Single select + start drag for all selected
        if (!el.hasAttribute('data-selected')) {
          selectElements([el], false);
        }

        const svgRect = svg.getBoundingClientRect();
        const viewBox = svg.getAttribute('viewBox')?.split(/[\s,]+/).map(Number) || [0, 0, svgRect.width, svgRect.height];
        const scaleX = viewBox[2] / svgRect.width;
        const scaleY = viewBox[3] / svgRect.height;
        const startX = e.clientX;
        const startY = e.clientY;

        // Capture original transforms for all selected
        const allSelected = Array.from(container.querySelectorAll('[data-selected]'));
        const origTransforms = allSelected.map(sel => {
          const t = sel.getAttribute('transform') || '';
          const m = t.match(/translate\(([-\d.]+)[,\s]+([-\d.]+)\)/);
          return { el: sel, origT: t, tx: m ? parseFloat(m[1]) : 0, ty: m ? parseFloat(m[2]) : 0 };
        });

        function onMove(me) {
          const dx = (me.clientX - startX) * scaleX;
          const dy = (me.clientY - startY) * scaleY;
          origTransforms.forEach(({ el: s, origT, tx, ty }) => {
            const clean = origT.replace(/translate\([^)]+\)/, '').trim();
            s.setAttribute('transform', `translate(${tx + dx}, ${ty + dy}) ${clean}`.trim());
          });
        }
        function onUp() {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
          saveSvg();
        }
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      });
    });

    // Drag on empty space -> selection rectangle
    svg.addEventListener('mousedown', (e) => {
      if (e.target !== svg) return;
      const svgRect = svg.getBoundingClientRect();
      const viewBox = svg.getAttribute('viewBox')?.split(/[\s,]+/).map(Number) || [0, 0, svgRect.width, svgRect.height];
      const scaleX = viewBox[2] / svgRect.width;
      const scaleY = viewBox[3] / svgRect.height;
      const startX = (e.clientX - svgRect.left) * scaleX + viewBox[0];
      const startY = (e.clientY - svgRect.top) * scaleY + viewBox[1];

      if (!e.shiftKey) clearSelection();

      // Create selection rectangle
      const ns = 'http://www.w3.org/2000/svg';
      const rect = document.createElementNS(ns, 'rect');
      rect.setAttribute('data-sel-rect', 'true');
      rect.setAttribute('x', startX);
      rect.setAttribute('y', startY);
      rect.setAttribute('width', 0);
      rect.setAttribute('height', 0);
      rect.setAttribute('fill', 'rgba(59,130,246,0.1)');
      rect.setAttribute('stroke', '#3B82F6');
      rect.setAttribute('stroke-width', '1');
      rect.setAttribute('stroke-dasharray', '4,2');
      svg.appendChild(rect);
      selectionRectRef.current = rect;

      function onMove(me) {
        const curX = (me.clientX - svgRect.left) * scaleX + viewBox[0];
        const curY = (me.clientY - svgRect.top) * scaleY + viewBox[1];
        const x = Math.min(startX, curX);
        const y = Math.min(startY, curY);
        rect.setAttribute('x', x);
        rect.setAttribute('y', y);
        rect.setAttribute('width', Math.abs(curX - startX));
        rect.setAttribute('height', Math.abs(curY - startY));
      }

      function onUp(me) {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);

        const rx = parseFloat(rect.getAttribute('x'));
        const ry = parseFloat(rect.getAttribute('y'));
        const rw = parseFloat(rect.getAttribute('width'));
        const rh = parseFloat(rect.getAttribute('height'));
        rect.remove();
        selectionRectRef.current = null;

        if (rw < 3 && rh < 3) return; // too small, was just a click

        // Find elements inside the rectangle
        const hits = [];
        interactable.forEach(el => {
          try {
            const bbox = el.getBBox();
            const cx = bbox.x + bbox.width / 2;
            const cy = bbox.y + bbox.height / 2;
            if (cx >= rx && cx <= rx + rw && cy >= ry && cy <= ry + rh) {
              hits.push(el);
            }
          } catch {}
        });
        if (hits.length > 0) selectElements(hits, e.shiftKey);
      }

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    });
  }, [editedSvg]);

  // Keyboard shortcuts -- apply to all selected
  useEffect(() => {
    function onKey(e) {
      if (selected.length === 0) return;
      const svg = containerRef.current?.querySelector('svg');
      if (!svg) return;

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        selected.forEach(el => el.remove());
        setSelected([]);
        saveSvg();
      }
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        selected.forEach(el => {
          const t = el.getAttribute('transform') || '';
          const m = t.match(/scale\(([-\d.]+)\)/);
          const s = (m ? parseFloat(m[1]) : 1) * 1.15;
          el.setAttribute('transform', m ? t.replace(/scale\([^)]+\)/, `scale(${s.toFixed(2)})`) : `${t} scale(${s.toFixed(2)})`.trim());
        });
        saveSvg();
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        selected.forEach(el => {
          const t = el.getAttribute('transform') || '';
          const m = t.match(/scale\(([-\d.]+)\)/);
          const s = Math.max(0.1, (m ? parseFloat(m[1]) : 1) * 0.85);
          el.setAttribute('transform', m ? t.replace(/scale\([^)]+\)/, `scale(${s.toFixed(2)})`) : `${t} scale(${s.toFixed(2)})`.trim());
        });
        saveSvg();
      }
      // Select all with Ctrl/Cmd+A
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        const all = Array.from(svg.querySelectorAll('circle, ellipse, rect:not([data-sel-rect]), path, line, text, polygon, g'));
        selectElements(all, false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, onChange]);

  const count = selected.length;

  return (
    <div className="space-y-2">
      <div ref={containerRef} className="bg-white rounded-lg border-2 border-dashed border-gray-200 aspect-square w-full [&>svg]:w-full [&>svg]:h-full" />
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-gray-400">
          {count > 1 ? `${count} selected \u2014 Drag to move | +/- resize | Del remove` :
           count === 1 ? 'Drag to move | +/- resize | Del remove | Shift+click add' :
           'Click to select | Drag to lasso | Shift+click multi-select'}
        </span>
        {count > 0 && (
          <button onClick={() => {
            selected.forEach(el => el.remove());
            setSelected([]);
            saveSvg();
          }} className="text-[10px] px-2 py-0.5 bg-red-100 text-red-600 rounded hover:bg-red-200">
            Delete {count > 1 ? `${count} Selected` : 'Selected'}
          </button>
        )}
      </div>
    </div>
  );
}
